import { typography as currentTypography } from "../current/typography";
import { fontFamilies, fontWeights } from "../primitives/typography";

/** Inter is the text face; Archivo distinguishes money and display figures. */
export type Typeface = "inter" | "archivo";

export const typographyRendering = {
  family: (typeface: Typeface) =>
    typeface === "inter" ? fontFamilies.standard : fontFamilies.numbers,
  weight: <Weight extends keyof typeof fontWeights>(weight: Weight) =>
    fontWeights[weight],
} as const;

export type FontWeightValue = (typeof fontWeights)[keyof typeof fontWeights];

export type TextTransform = "uppercase" | "lowercase" | "capitalize" | "none";

export interface TypographyMetrics {
  typeface: Typeface;
  fontSize: number;
  fontWeight: FontWeightValue;
  lineHeight: number;
  letterSpacing: number;
  textTransform?: TextTransform;
}

const { display: displayMetrics, ...scale } = currentTypography;

export const typographyScale = scale;

export type TypographyScale = typeof typographyScale;
export type TypographyCategory = keyof TypographyScale;
export type TypographyVariant<T extends TypographyCategory> =
  keyof TypographyScale[T];

/** Dot-notation union of every variant, e.g. "heading.sm" | "body.md". */
export type TypographyVariantPath =
  | "display"
  | {
      [C in TypographyCategory & string]: `${C}.${TypographyVariant<C> &
        string}`;
    }[TypographyCategory & string];

/** The scale's shape with every leaf replaced by a platform style. */
type TypographyVariants<Style> = {
  [C in TypographyCategory]: { [V in keyof TypographyScale[C]]: Style };
};

export type TypographyTree<Style> = TypographyVariants<Style> & {
  display: Style;
};

/** Build a platform's `typography` object from the scale, keeping every category and variant key. */
export const mapTypographyScale = <Style>(
  toStyle: (metrics: TypographyMetrics) => Style
): TypographyTree<Style> => {
  const tree = {} as Record<string, Record<string, Style>>;
  for (const [category, variants] of Object.entries(typographyScale)) {
    tree[category] = {};
    for (const [variant, metrics] of Object.entries(variants)) {
      tree[category][variant] = toStyle(metrics as TypographyMetrics);
    }
  }
  const variants = tree as TypographyVariants<Style>;
  return {
    ...variants,
    display: toStyle(displayMetrics),
  };
};
